import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import i18next from 'i18next';
import Immutable, { List } from 'immutable';

import { Button } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink, Paragraph, Node, Block, View } from '@uncinc/react-drupal-core';
import {
  Container,
  WysiwygField,
  TitleHeaderBlock,
  Badge, Breadcrumbs,
} from '../../../../components';

import {
  BLOCK_ID_COURSE_INFORMATION,
} from '../../../../config/constants';
import { formatMoneyFromString } from '../../../../helpers';

import './style.scss';

const NodeCourseFull = ({ entity, className }) => {
  const classes = classNames(className, 'NodeCourseFull');
  const nid = entity.getIn(['nid', 0, 'value']);
  const title = entity.getIn(['title', 0, 'value']);
  const body = entity.getIn(['body', 0, 'processed'], '');
  const courses = entity.get('field_courses', Immutable.List());
  const sortedCourses = courses.sort((a, b) => {
    const aDate = new Date(a.getIn(['entity', 'field_date_range', 0, 'value']));
    const bDate = new Date(b.getIn(['entity', 'field_date_range', 0, 'value']));
    return aDate - bDate;
  });
  const theme = entity.getIn(['field_theme', 0, 'entity', 'name', 0, 'value'], '');
  const themeId = entity.getIn(['field_theme', 0, 'target_id']);
  const relatedContent = entity.getIn(['field_related_content'], List());
  const courseType = entity.getIn(['field_training_type', 0, 'entity', 'name', 0, 'value'], '');

  const firstCourse = sortedCourses.get(0);
  const location = firstCourse.getIn(['entity', 'field_location', 0, 'value'], '');
  const startDate = moment(firstCourse.getIn(['entity', 'field_date_range', 0, 'value'], '-'));
  const endDate = moment(firstCourse.getIn(['entity', 'field_date_range', 0, 'end_value'], '-'));
  const cost = firstCourse.getIn(['entity', 'field_cost', 0, 'value']);
  const link = firstCourse.getIn(['entity', 'field_sign_up_link', 0, 'uri'], '-');
  const maxSpots = firstCourse.getIn(['entity', 'field_seats_available', 0, 'value'], false);
  const spotsTaken = firstCourse.getIn(['entity', 'field_seats_taken', 0, 'value'], 0);
  const availableSpots = maxSpots ? Math.max(maxSpots - spotsTaken, 0) : '0';
  const availableSpotsFlag = availableSpots > 0;

  const renderDate = () => {
    if (startDate.isSame(endDate, 'date')) {
      return `${startDate.format('H:mm uur')} - ${endDate.format('H:mm uur')}`;
    } else {
      return (
        <>
          <div>{startDate.format('DD-MM-YYYY H:mm')}</div>
          <div>{endDate.format('DD-MM-YYYY H:mm')}</div>
        </>
      );
    }
  };

  return (
    <article typeof="schema:WebPage" className={classes}>
      {/* Breadcrumbs */}
      <Container small>
        <Breadcrumbs includeCurrent />
      </Container>

      <div className="NodeCourseFull__content">
        <div className="NodeCourseFull__contentWrapper">
          <Container small>

            {theme && (
              <Badge theme={theme} type={courseType} />
            )}

            <h1 className="node--title">{title}</h1>

            {body ? (
              <WysiwygField
                property="schema:text"
                className="node--body"
                html={body}
              />
            ) : null}

            <div className="node--first-training">
              <h3>{sortedCourses.size > 1 ? i18next.t('first_upcoming_training') : i18next.t('information')}</h3>
              <div className="course-data">
                <div className="course-data__row">
                  <div className="course-data__row__col label">
                    {i18next.t('location')}
                  </div>
                  <div className="course-data__row__col value">
                    {location}
                  </div>
                </div>
                <div className="course-data__row">
                  <div className="course-data__row__col label">
                    {i18next.t('date')}
                  </div>
                  <div className="course-data__row__col value">
                    {startDate.format('DD-MM-YYYY')}
                  </div>
                </div>
                <div className="course-data__row">
                  <div className="course-data__row__col label">
                    {i18next.t('date_time')}
                  </div>
                  <div className="course-data__row__col value">
                    {renderDate()}
                  </div>
                </div>
                <div className="course-data__row">
                  <div className="course-data__row__col label">
                    {i18next.t('costs')}
                  </div>
                  <div className="course-data__row__col value">
                    {cost ? formatMoneyFromString(cost) : '-'}
                  </div>
                </div>
                <div className="course-data__row">
                  <div className="course-data__row__col label">
                    {i18next.t('available_seats')}
                  </div>
                  <div className="course-data__row__col value">
                    {i18next.t('training_spots', {
                      availableSpots: availableSpots,
                      maxSpots: maxSpots,
                    })}
                  </div>
                </div>
              </div>
              <div className="buttons">
                {availableSpotsFlag && (
                  <Button
                    className="Button--primary external-icon"
                    href={link}>
                    {i18next.t('signup_cta')}
                  </Button>
                )}
                {sortedCourses.size > 1 && (
                  <CoreLink to="#courses" className="more-dates">
                    {i18next.t('more_dates_locations')}
                  </CoreLink>
                )}
              </div>
            </div>

          </Container>

          {entity.get('field_paragraphs') ? (
            <div className="node--paragraphs-wrapper">
              {entity.get('field_paragraphs', List()).map((paragraph) => {
                const id = paragraph.get('target_id');
                const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
                return (
                  <Paragraph
                    key={`paragraph:${id}:${language}`}
                    id={id}
                    entity={paragraph.get('entity')}
                  />
                );
              })}
            </div>
          ) : null}

          <Container small className="course-dates" id="courses">
            {sortedCourses.size > 1 && (
              <>
                <div className="row node--courses-heading">
                  <div className="col-12">
                    <h2>{i18next.t('training_offered_on')}:</h2>
                  </div>
                </div>

                <div className="row">
                  {sortedCourses.slice(0, 10).map((course, index) => {
                    const id = course.getIn(['target_id']);
                    return index !== 0 && (
                      <Node
                        key={`course-${id}`}
                        entity={course.get('entity')}
                        viewMode="teaser"
                      />
                    );
                  })}
                </div>
              </>
            )}

            <div className="row">
              <div className="col-12">
                <Block id={BLOCK_ID_COURSE_INFORMATION} />
              </div>
            </div>
          </Container>

          <TitleHeaderBlock
            title={`Meer trainingen over ${theme}`}
            linkText="Alle trainingen"
            linkUrl={`/nl/trainingsaanbod?theme=${themeId}`}
          >
            {/* View content that is being rendered. */}
            <View
              id="courses"
              display="recent"
              userSelectedEntities={relatedContent}
              filters={{
                field_theme_target_id: themeId,
              }}
              args={[nid]}
            />
          </TitleHeaderBlock>
        </div>
      </div>


    </article>
  );
};

export default NodeCourseFull;
